import React, { useEffect, useState } from "react";
import { FileText, Download, Upload, Clock, CheckCircle2, AlertCircle, Euro, Handshake, Sparkles, X, PenTool, Eye } from "lucide-react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import SignaturePadModal from "./SignaturePadModal";

const PRIMARY = "#4F1B7C";
const PINK = "#EC1F7C";

const STATUS = {
  sent:           { label: "À signer",          color: "#0ea5e9", bg: "#e0f2fe" },
  downloaded:     { label: "Téléchargée",       color: "#6366f1", bg: "#e0e7ff" },
  partner_signed: { label: "Signée — attente club", color: "#a855f7", bg: "#f3e8ff" },
  fully_signed:   { label: "Signée des 2 parties",  color: "#10b981", bg: "#d1fae5" },
  completed:      { label: "Re-déposée",        color: "#a855f7", bg: "#f3e8ff" },
  paid:           { label: "Soldée",            color: "#10b981", bg: "#d1fae5" },
  cancelled:      { label: "Annulée",           color: "#ef4444", bg: "#fee2e2" },
};

const fmtEur = (cents) => `${(Number(cents || 0) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 0 })} €`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; } };

// Token customer (différent du token admin) — l'intercepteur api.js ne l'ajoute pas automatiquement
function customerAuthHeaders() {
  const t = localStorage.getItem("msc_customer_token");
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
}

// Ouvre l'aperçu HTML imprimable de la convention (token customer dans l'URL via fetch+blob)
async function previewConventionHtml(cid) {
  try {
    const t = localStorage.getItem("msc_customer_token") || localStorage.getItem("kap_token") || "";
    const r = await fetch(`${API}/sport/conventions/${cid}/render-html`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!r.ok) {
      toast.error("Aperçu indisponible");
      return;
    }
    const html = await r.text();
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch (e) {
    toast.error("Erreur aperçu convention");
  }
}

export default function PartnerConventionsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // convention to view/upload

  const reload = async () => {
    try {
      const r = await api.get("/sport/partner/conventions", customerAuthHeaders()).then((r) => r.data?.rows || []);
      setRows(r);
    } catch (e) {
      // 403 silencieux : pas de carte affichée
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  if (loading) return null;
  if (!rows.length) return null;

  return (
    <div id="conventions" className="rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden" data-testid="partner-conventions">
      <div className="px-5 py-3 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${PRIMARY}, #2c0e4a)` }}>
        <div>
          <div className="text-pink-300 font-black tracking-[0.3em] text-[10px] uppercase">Documents</div>
          <div className="text-white font-blackhawk italic uppercase text-base sm:text-lg leading-tight">Mes conventions</div>
        </div>
        <FileText className="text-pink-300" size={18}/>
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map((c) => {
          const s = STATUS[c.status] || STATUS.sent;
          return (
            <li key={c.id} className="px-5 py-4 flex items-start gap-3" data-testid={`conv-card-${c.id}`}>
              <div className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: `${s.color}1a`, color: s.color }}>
                <FileText size={18}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-800">Convention {c.season}</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black tracking-widest uppercase"
                    style={{ color: s.color, background: s.bg }}>{s.label}</span>
                </div>
                <div className="text-slate-500 text-xs mt-0.5">
                  Reçue le {fmtDate(c.sent_at)} · {fmtEur(c.items_total_cents)}
                  {c.deal_type === "exchange" && <span className="ml-1 inline-flex items-center gap-1 text-amber-600"><Handshake size={11}/> Échange</span>}
                  {c.deal_type === "mixed" && <span className="ml-1 inline-flex items-center gap-1 text-purple-600"><Sparkles size={11}/> Mixte</span>}
                </div>
                <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                  <button onClick={() => setOpen(c)}
                    className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold inline-flex items-center gap-1.5"
                    data-testid={`conv-detail-${c.id}`}>
                    Détails
                  </button>
                  <button onClick={() => previewConventionHtml(c.id)}
                    className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 font-bold inline-flex items-center gap-1.5"
                    data-testid={`conv-preview-${c.id}`}>
                    <Eye size={12}/> Aperçu
                  </button>
                  {(c.status === "sent" || c.status === "downloaded") && (
                    <button onClick={() => setOpen({ ...c, _action: "sign" })}
                      className="text-xs px-3 py-1.5 rounded-lg text-white font-bold inline-flex items-center gap-1.5 shadow-sm"
                      style={{ background: `linear-gradient(135deg, ${PINK}, #ff3a8c)` }}
                      data-testid={`conv-sign-${c.id}`}>
                      <PenTool size={12}/> Signer en ligne
                    </button>
                  )}
                  {c.status === "partner_signed" && (
                    <span className="text-xs text-purple-600 font-bold inline-flex items-center gap-1">
                      <Clock size={12}/> En attente signature du club
                    </span>
                  )}
                  {c.status === "fully_signed" && (
                    <span className="text-xs text-emerald-600 font-bold inline-flex items-center gap-1">
                      <CheckCircle2 size={12}/> Signée des 2 parties
                    </span>
                  )}
                  {c.status === "paid" && (
                    <span className="text-xs text-emerald-600 font-bold inline-flex items-center gap-1">
                      <CheckCircle2 size={12}/> Validée par le club
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {open && (
        <ConventionDetailModal convention={open} onClose={() => setOpen(null)} onAction={reload}/>
      )}
    </div>
  );
}

function ConventionDownloadBtn({ conv, onDownloaded }) {
  const handleClick = async () => {
    try { await api.post(`/sport/partner/conventions/${conv.id}/mark-downloaded`, {}, customerAuthHeaders()); } catch {}
    if (conv.convention_pdf_url) {
      window.open(conv.convention_pdf_url, "_blank", "noopener,noreferrer");
    } else {
      toast.info("Le PDF n'est pas encore disponible. Contactez votre commercial.");
    }
    onDownloaded?.();
  };
  return (
    <button onClick={handleClick} className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 font-bold inline-flex items-center gap-1.5">
      <Download size={12}/> Télécharger
    </button>
  );
}

function ConventionDetailModal({ convention, onClose, onAction }) {
  const c = convention;
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signing, setSigning] = useState(c?._action === "sign");

  const handleSignSubmit = async ({ signatureDataUrl, signerName, signerTitle }) => {
    try {
      await api.post(`/sport/partner/conventions/${c.id}/sign`, {
        role: "partner",
        signature_data_url: signatureDataUrl,
        signer_name: signerName,
        signer_title: signerTitle,
      }, customerAuthHeaders());
      toast.success("Convention signée. Le club a été notifié.");
      onAction?.();
      onClose();
    } catch (e) {
      throw e;
    }
  };

  const handleUpload = async () => {
    if (!file) { toast.error("Sélectionnez un fichier"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 8 Mo)"); return; }
    setBusy(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = String(reader.result || "").split(",")[1] || "";
        try {
          await api.post(`/sport/partner/conventions/${c.id}/upload`, {
            file_b64: b64,
            file_name: file.name,
          }, customerAuthHeaders());
          toast.success("Convention envoyée au club. Merci !");
          onAction?.();
          onClose();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Erreur upload");
        } finally { setBusy(false); }
      };
      reader.readAsDataURL(file);
    } catch (e) {
      setBusy(false);
      toast.error("Erreur lecture fichier");
    }
  };

  const canUpload = c.status === "sent" || c.status === "downloaded";

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-lg sm:rounded-3xl shadow-2xl max-h-[100vh] sm:max-h-[90vh] overflow-y-auto" data-testid="conv-detail-modal">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-pink-600 text-[10px] font-black uppercase tracking-widest">Convention</div>
            <h2 className="font-blackhawk italic uppercase text-xl" style={{ color: PRIMARY }}>{c.partner_name}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Saison</div>
              <div className="font-bold text-slate-800">{c.season}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Total</div>
              <div className="font-black text-slate-900">{fmtEur(c.items_total_cents)}</div>
            </div>
            {c.valid_until && (
              <div className="col-span-2">
                <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Valable jusqu'au</div>
                <div className="font-bold text-slate-800">{fmtDate(c.valid_until)}</div>
              </div>
            )}
          </div>

          <div>
            <div className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-2">Éléments inclus</div>
            <ul className="space-y-1 text-sm">
              {(c.items || []).map((it, i) => (
                <li key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-100">
                  <span className="text-slate-700"><span className="text-slate-400 text-xs mr-2">×{it.quantity}</span>{it.label}</span>
                  <span className="text-slate-500 tabular-nums text-xs">{fmtEur((it.unit_price_cents || 0) * (it.quantity || 1))}</span>
                </li>
              ))}
            </ul>
          </div>

          {c.deal_type === "exchange" || c.deal_type === "mixed" ? (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm">
              <div className="flex items-center gap-2 text-amber-800 font-bold mb-1"><Handshake size={14}/> Échange de service</div>
              <div className="text-amber-700 text-xs">Valorisé {fmtEur(c.exchange_amount_cents)} : {c.exchange_service || "—"}</div>
            </div>
          ) : null}

          {canUpload && (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 p-4 space-y-3">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 inline-flex items-center gap-1.5">
                <AlertCircle size={11}/> 2 options pour finaliser
              </div>

              {/* Option A — Signature en ligne */}
              <button onClick={() => setSigning(true)}
                className="w-full px-4 py-3 rounded-xl text-white font-black uppercase tracking-wider text-xs shadow-md inline-flex items-center justify-center gap-2"
                style={{ background: `linear-gradient(135deg, ${PINK}, #ff3a8c)` }} data-testid="conv-open-sign">
                <PenTool size={14}/> Signer en ligne (recommandé)
              </button>
              <p className="text-[10px] text-slate-400 italic text-center -mt-2">Signature électronique conforme eIDAS · Pas besoin d'imprimer</p>

              <div className="flex items-center gap-2 text-[10px] uppercase font-bold text-slate-300">
                <span className="h-px flex-1 bg-slate-200"/> ou <span className="h-px flex-1 bg-slate-200"/>
              </div>

              {/* Option B — Upload PDF complété */}
              <ol className="text-xs text-slate-600 space-y-1 list-decimal pl-4">
                <li>Téléchargez la convention PDF (bouton Aperçu plus haut).</li>
                <li>Imprimez-la, signez-la, scannez-la.</li>
                <li>Re-déposez le PDF complété ici.</li>
              </ol>
              <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-xs text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-bold file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100"
                data-testid="conv-upload-input"/>
              {file && (
                <div className="text-xs text-slate-500">📄 {file.name} · {(file.size / 1024).toFixed(1)} Ko</div>
              )}
              <button onClick={handleUpload} disabled={!file || busy}
                className="w-full px-4 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white font-bold text-xs disabled:opacity-50"
                data-testid="conv-upload-submit">
                {busy ? "Envoi…" : "Envoyer le PDF complété"}
              </button>
            </div>
          )}

          {c.status === "partner_signed" && (
            <div className="rounded-xl bg-purple-50 border border-purple-200 p-3 flex items-center gap-2">
              <Clock size={14} className="text-purple-600"/>
              <span className="text-purple-700 text-xs font-bold">Convention signée. En attente de la signature du président du club.</span>
            </div>
          )}
          {c.status === "fully_signed" && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2">
              <CheckCircle2 size={14} className="text-emerald-600"/>
              <span className="text-emerald-700 text-xs font-bold">Convention signée des 2 parties. Document final disponible via Aperçu.</span>
            </div>
          )}
          {c.status === "completed" && (
            <div className="rounded-xl bg-purple-50 border border-purple-200 p-3 flex items-center gap-2">
              <Clock size={14} className="text-purple-600"/>
              <span className="text-purple-700 text-xs font-bold">Convention reçue par le club. En attente de validation finale.</span>
            </div>
          )}
          {c.status === "paid" && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2">
              <CheckCircle2 size={14} className="text-emerald-600"/>
              <span className="text-emerald-700 text-xs font-bold">Convention validée et soldée. Bienvenue à bord !</span>
            </div>
          )}
        </div>
      </div>

      {/* Modal signature pad */}
      {signing && (
        <SignaturePadModal
          title="Signer la convention de partenariat"
          signerName={c.partner_signer_name || ""}
          signerTitle={c.partner_signer_title || "Représentant légal"}
          onClose={() => setSigning(false)}
          onSign={handleSignSubmit}
        />
      )}
    </div>
  );
}
